import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import type { LanguageCode } from '../locales';
import { translate, setActiveLanguage } from './translate';
import { bumpLanguageVersion, getLanguageVersion, subscribeLanguage } from './languageEvents';
import { useI18n } from '../context/LanguageContext';

/**
 * Pont entre l'API de traduction applicative et le LanguageContext existant.
 * On lit la langue depuis src/context/LanguageContext.tsx.
 * et on synchronise le module translate.ts à chaque changement de langue.
 */
export function useTranslation() {
  const { language, setLanguage } = useI18n();

  // Synchronise le module translate.ts avec la langue courante du contexte toons.
  useEffect(() => {
    setActiveLanguage(language as LanguageCode);
    bumpLanguageVersion();
  }, [language]);

  // Permet aux composants de se re-rendre quand la version de langue change.
  useSyncExternalStore(subscribeLanguage, getLanguageVersion, getLanguageVersion);

  const t = useCallback(
    (key: string, options?: Record<string, string | number>) => translate(key, options),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language],
  );

  const i18n = useMemo(
    () => ({
      language,
      resolvedLanguage: language,
      /**
       * Appelé par le sélecteur de langue du header porté depuis Universe.
       * En pratique, le header effectue window.location.replace() juste après,
       * donc ce callback met simplement à jour le contexte toons localement.
       */
      changeLanguage: async (code: string) => {
        setLanguage(code as 'fr' | 'en');
        setActiveLanguage(code as LanguageCode);
        bumpLanguageVersion();
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language],
  );

  return useMemo(() => ({ t, i18n }), [t, i18n]);
}
